import { AlertTriangle, Check } from "lucide-react";

import type { FieldProposal } from "@/api/types";

import { SourceTag } from "./ConfidenceIndicator";

/**
 * What the machine read for one field, and what it read instead (§5, §14).
 *
 * A field used to show one value and one source tag. That was true when a single
 * provider spoke; now the barcode, the OCR and the vision model each say
 * something, the source priority order picks a winner, and the losers are worth
 * more than a footnote — they are the fastest correction available.
 *
 * Two states are shown differently on purpose:
 *
 * * **contested** — sources read it differently. That is a question for the
 *   operator, and the alternatives are offered as one tap each rather than
 *   making them retype a value HD-Vault already read.
 * * **corroborated** — a second source agreed. Quiet, and never louder than the
 *   value itself: agreement is reassuring, not conclusive.
 *
 * Nothing here recomputes anything. The winner, the ranking and the contested
 * flag all come from the server, which is the only place that knows the order.
 */
export function FieldProvenance({
  proposal,
  onChoose,
}: {
  proposal: FieldProposal;
  onChoose: (value: string) => void;
}) {
  const alternatives = proposal.alternatives_json.filter(
    (entry) => entry.value !== (proposal.accepted_value ?? proposal.original_value),
  );

  if (!proposal.contested && alternatives.length === 0) {
    return proposal.corroborated ? (
      <p className="flex items-center gap-1 text-[11px] text-ink-subtle">
        <Check className="size-3 shrink-0 text-success" aria-hidden />
        Deux lectures concordantes.
      </p>
    ) : null;
  }

  return (
    <div className="flex flex-col gap-1.5">
      {proposal.contested && (
        <p className="flex items-start gap-1 text-[11px] text-warning">
          <AlertTriangle className="mt-px size-3 shrink-0" aria-hidden />
          <span>Deux lectures différentes. Choisissez celle qui est bonne.</span>
        </p>
      )}
      {alternatives.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {alternatives.map((entry) => (
            <li key={`${entry.source}-${entry.value}`}>
              <button
                type="button"
                onClick={() => onChoose(entry.value)}
                className="flex items-center gap-1.5 rounded-md border border-border bg-surface-sunken px-2 py-1 text-[11px] text-ink-muted transition-colors hover:border-border-strong hover:text-ink"
              >
                <span className="font-mono">{entry.value}</span>
                <SourceTag source={entry.source} provider={entry.provider} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
